import * as React from "react";
import Badge from "../Badge/index";
import type { TableCellProps, TableCol, TableProps } from "../types";
import IconButton from "../IconButton/index";
import Spacer from "../Spacer/index";

function TableCell(props: TableCellProps) {
  function actionClick(
    action: TableProps["actions"][number],
    item: TableProps["data"][number]
  ) {
    return () => action.handleClick(item);
  }

  function cellValue() {
    const _col = props.col;
    let value = "-";
    if (isStringColumn()) {
      value = props.rowData[_col as string];
    } else if (isAdvancedColumnType()) {
      value = props.rowData[(_col as TableCol).name];
    }
    return value;
  }

  function isStringColumn() {
    return typeof props.col === "string";
  }

  function isAdvancedColumnType() {
    return typeof props.col === "object" && "name" in props.col;
  }

  function isActionsColumn() {
    return (
      props.col === "actions" ||
      (typeof props.col === "object" && props.col.name === "actions")
    );
  }

  function displayBadge() {
    const _col = props.col as TableCol;
    if (typeof _col.badge?.shouldDisplayBadge === "function") {
      return _col.badge.shouldDisplayBadge(props.rowData);
    }
    return !!(_col.badge?.position || _col.badge?.badgeText);
  }

  function badgePosition() {
    const _col = props.col as TableCol;
    return _col.badge?.position;
  }

  function badgeText() {
    const _col = props.col as TableCol;
    return _col.badge?.badgeText || props.rowData[_col.name];
  }

  function badgeLabel() {
    const _col = props.col as TableCol;
    return _col.badge?.ariaLabel;
  }

  function badgeVariant() {
    const _col = props.col as TableCol;
    if (typeof _col.badge?.variantSelector === "function") {
      return _col.badge.variantSelector(props.rowData);
    }
    return _col.badge?.variant;
  }

  function isEmptyValue() {
    return (
      cellValue() === undefined || cellValue() === "" || cellValue() === null
    );
  }

  return (
    <>
      {isActionsColumn() ? (
        <>
          {props.actions?.map((action, index) => (
            <span className={props.classNames?.iconSpan} key={index}>
              <IconButton
                label={action.label}
                handleClick={actionClick(action, props.rowData)}
                icon={action.icon}
              />
            </span>
          ))}
        </>
      ) : null}
      {isStringColumn() ? <>{cellValue()}</> : null}
      {isAdvancedColumnType() ? (
        <>
          {!displayBadge() ? (
            <>{cellValue()}</>
          ) : (
            <>
              <>
                {badgePosition() === "surround" ? (
                  <Badge
                    badgeText={badgeText()}
                    ariaLabel={badgeLabel()}
                    variant={badgeVariant()}
                  />
                ) : null}
                {badgePosition() === "left" ? (
                  <>
                    <Badge
                      badgeText={badgeText()}
                      ariaLabel={badgeLabel()}
                      variant={badgeVariant()}
                    />
                    {!isEmptyValue() ? <Spacer x={2} /> : null}
                    {cellValue()}
                  </>
                ) : null}
                {badgePosition() === "right" ? (
                  <>
                    {cellValue()}
                    {!isEmptyValue() ? <Spacer x={2} /> : null}
                    <Badge
                      badgeText={badgeText()}
                      ariaLabel={badgeLabel()}
                      variant={badgeVariant()}
                    />
                  </>
                ) : null}
              </>
            </>
          )}
        </>
      ) : null}
    </>
  );
}

export default TableCell;
